import React, { useEffect, useState } from "react";
import client, { formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

export default function AdminChallenges() {
    const [list, setList] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(blank());
    const [shopItems, setShopItems] = useState([]);

    function blank() {
        return {
            title: "",
            exercise_type: "push-up",
            reps_required: 10,
            time_limit_seconds: 60,
            difficulty: "easy",
            level: 1,
            instruction: "",
            safety_notes: "",
            xp_reward: 50,
            required_item_ids: [],
        };
    }

    const load = () => client.get("/admin/challenges").then((r) => setList(r.data));
    const loadShop = () => client.get("/admin/shop/items").then((r) => setShopItems(r.data || []));
    useEffect(() => {
        load();
        loadShop();
    }, []);

    const create = async (e) => {
        e.preventDefault();
        try {
            await client.post("/admin/challenges", form);
            toast.success("Challenge created");
            setForm(blank());
            setShowForm(false);
            load();
        } catch (err) {
            toast.error(formatErr(err));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this challenge?")) return;
        await client.delete(`/admin/challenges/${id}`);
        toast.success("Deleted");
        load();
    };

    return (
        <div>
            <div className="flex items-end justify-between">
                <div>
                    <span className="label-cap text-xs text-neon-green">Builder</span>
                    <h1 className="font-display font-black text-4xl uppercase tracking-tight mt-1">Challenges</h1>
                </div>
                <button
                    onClick={() => setShowForm(!showForm)}
                    data-testid="admin-new-challenge"
                    className="btn-neon px-4 py-2 flex items-center gap-2"
                >
                    <Plus size={16} /> New
                </button>
            </div>

            {showForm && (
                <form onSubmit={create} className="mt-6 surface-2 p-4 grid grid-cols-2 gap-3" data-testid="admin-challenge-form">
                    <Field label="Title" full>
                        <input
                            required
                            data-testid="cf-title"
                            value={form.title}
                            onChange={(e) => setForm({ ...form, title: e.target.value })}
                            className={inp}
                        />
                    </Field>
                    <Field label="Exercise Type">
                        <input
                            required
                            data-testid="cf-type"
                            value={form.exercise_type}
                            onChange={(e) => setForm({ ...form, exercise_type: e.target.value })}
                            className={inp}
                        />
                    </Field>
                    <Field label="Level">
                        <input
                            type="number"
                            min={1}
                            max={10}
                            data-testid="cf-level"
                            value={form.level}
                            onChange={(e) => setForm({ ...form, level: +e.target.value })}
                            className={inp}
                        />
                    </Field>
                    <Field label="Reps Required">
                        <input
                            type="number"
                            min={1}
                            data-testid="cf-reps"
                            value={form.reps_required}
                            onChange={(e) => setForm({ ...form, reps_required: +e.target.value })}
                            className={inp}
                        />
                    </Field>
                    <Field label="Time Limit (sec)">
                        <input
                            type="number"
                            min={5}
                            data-testid="cf-time"
                            value={form.time_limit_seconds}
                            onChange={(e) => setForm({ ...form, time_limit_seconds: +e.target.value })}
                            className={inp}
                        />
                    </Field>
                    <Field label="Difficulty">
                        <select
                            data-testid="cf-difficulty"
                            value={form.difficulty}
                            onChange={(e) => setForm({ ...form, difficulty: e.target.value })}
                            className={inp}
                        >
                            <option value="easy">Easy</option>
                            <option value="medium">Medium</option>
                            <option value="hard">Hard</option>
                            <option value="elite">Elite</option>
                        </select>
                    </Field>
                    <Field label="XP Reward">
                        <input
                            type="number"
                            min={10}
                            data-testid="cf-xp"
                            value={form.xp_reward}
                            onChange={(e) => setForm({ ...form, xp_reward: +e.target.value })}
                            className={inp}
                        />
                    </Field>
                    <Field label="Instruction" full>
                        <textarea
                            required
                            rows={2}
                            data-testid="cf-instruction"
                            value={form.instruction}
                            onChange={(e) => setForm({ ...form, instruction: e.target.value })}
                            className={inp}
                        />
                    </Field>
                    <Field label="Safety Notes" full>
                        <textarea
                            rows={2}
                            data-testid="cf-safety"
                            value={form.safety_notes}
                            onChange={(e) => setForm({ ...form, safety_notes: e.target.value })}
                            className={inp}
                        />
                    </Field>
                    <Field label="Required Gear (any-of unlocks this challenge)" full>
                        <div className="grid grid-cols-2 gap-2" data-testid="cf-required-items">
                            {shopItems.length === 0 ? (
                                <div className="col-span-2 text-xs text-zinc-500">No shop items configured yet.</div>
                            ) : (
                                shopItems.map((it) => {
                                    const checked = form.required_item_ids.includes(it.id);
                                    return (
                                        <label
                                            key={it.id}
                                            data-testid={`cf-req-${it.id}`}
                                            className={`flex items-center gap-2 p-2 border cursor-pointer ${
                                                checked ? "border-neon-green bg-neon-green/5" : "border-charcoal-500"
                                            }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={(e) => {
                                                    const next = e.target.checked
                                                        ? [...form.required_item_ids, it.id]
                                                        : form.required_item_ids.filter((i) => i !== it.id);
                                                    setForm({ ...form, required_item_ids: next });
                                                }}
                                            />
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-bold truncate">{it.name}</div>
                                                <div className="text-[10px] text-zinc-500 uppercase">
                                                    {it.category} · {it.price_noex} $NoEx
                                                </div>
                                            </div>
                                        </label>
                                    );
                                })
                            )}
                        </div>
                    </Field>
                    <button type="submit" data-testid="cf-submit" className="btn-primary py-3 col-span-2">
                        Create Challenge
                    </button>
                </form>
            )}

            <div className="mt-6 space-y-2">
                {list.map((c) => (
                    <div key={c.id} className="surface-1 p-3 flex items-center gap-3" data-testid={`adm-ch-${c.id}`}>
                        <div className="w-12 text-center">
                            <div className="label-cap text-[10px] text-zinc-500">Lv</div>
                            <div className="font-display font-black text-2xl text-neon-green">{c.level}</div>
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-display font-bold uppercase text-sm truncate">{c.title}</div>
                            <div className="text-xs text-zinc-400 mt-1">
                                {c.reps_required} reps · {c.time_limit_seconds}s · {c.difficulty} · +{c.xp_reward} XP
                            </div>
                            {c.required_item_ids?.length > 0 && (
                                <div
                                    className="text-[10px] text-neon-orange uppercase tracking-wider mt-1"
                                    data-testid={`adm-ch-locked-${c.id}`}
                                >
                                    🔒 Requires {c.required_item_ids.length} gear item{c.required_item_ids.length > 1 ? "s" : ""}
                                </div>
                            )}
                        </div>
                        <button
                            onClick={() => remove(c.id)}
                            data-testid={`adm-ch-del-${c.id}`}
                            className="w-9 h-9 surface-3 flex items-center justify-center hover:border-neon-orange"
                        >
                            <Trash2 size={14} className="text-neon-orange" />
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}

const inp = "w-full bg-charcoal-700 border border-charcoal-500 px-3 py-2 text-white text-sm focus:border-neon-orange focus:outline-none";

function Field({ label, full, children }) {
    return (
        <div className={full ? "col-span-2" : ""}>
            <label className="label-cap text-[10px] text-zinc-500">{label}</label>
            <div className="mt-1">{children}</div>
        </div>
    );
}
